import {
  Avatar,
  Box,
  Column,
  Columns,
  FlexBox,
  IconButton,
  SelectionList,
  SelectionListChangeEvent,
  Tooltip,
} from '@dixa/lib-shared-habla';
import type {
  SearchFilterCondition,
  SearchFilterField,
  SearchSortField,
} from 'lib-search-types';
import type { SearchFilterChangeEvent } from 'lib-search-ui';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { SortBy } from 'lib-shared-legacy-types';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUnmount } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { appActions } from 'app/app.actions';
import { maxResultsCount } from 'app/views/layout/views/search/utils/results/maxResultsCount';
import { convertSortStringToObject } from 'app/views/layout/views/search/utils/sort/convertSort';
import { ConversationChip } from 'components/conversation';
import {
  CommandLauncherAction,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { getConversationTitle } from 'utils/conversation';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { getUserDisplayName } from 'utils/user';

import styles from '../../commandLauncher.module.scss';
import { getCommandLauncherActionItems } from '../../utils/getCommandLauncherActionSelectionListItems';

import { ConversationScopeListPreview } from './components/preview/conversationScopeListPreview';
import { ConversationScopeListToolbar } from './components/toolbar/conversationScopeListToolbar';

/** Formats the total results, which the search API caps at `maxResultsCount`. */
const formatResultsCount = (total = 0): string =>
  total >= maxResultsCount
    ? `${maxResultsCount.toLocaleString(navigator.language)}+`
    : total.toLocaleString(navigator.language);

/** Represents the conversation scope list of the command launcher. */
export const ConversationScopeList = (): JSX.Element | null => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const {
    actions: allActions,
    isActive,
    query,
    scope,
    createAction,
    performAction,
    registerActions,
    setAsyncStatus,
  } = useCommandLauncher();

  const isInScope = scope === CommandLauncherScope.Conversation;

  // Not using scopeQuery since it is truncated at the next '#' in the query.
  const term = query.slice(1).trim();
  const isStrict =
    term.length > 1 && term.startsWith('"') && term.endsWith('"');
  const searchQuery = isStrict ? term.slice(1, -1).trim() : term;

  const [filters, setFilters] = useState<
    readonly SearchFilterCondition<'conversations'>[]
  >([]);

  const [sort, setSort] = useState<
    SortBy<SearchSortField<'conversations'>> | ''
  >('');

  const resultsRef = useRef<HTMLDivElement>(null);
  const resultsScrollTopRef = useRef<number>();

  const [previewedCsid, setPreviewedCsid] = useState<number>();
  const isPreviewing = previewedCsid !== undefined;

  // The query is always set, so sorting must be kept when searching.
  const [
    readSearchConversations,
    {
      results: conversations,
      meta,
      isUninitialized,
      isLoading,
      isFetching,
      isSuccess,
      isError,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    },
  ] = useLazyReadSearchInfiniteQuery<'conversations', true>({
    sortOnSearch: true,
  });

  useEffect(() => {
    if (isInScope && searchQuery) {
      readSearchConversations(
        {
          scope: 'conversations',
          query: searchQuery,
          isStrict,
          filters: filters.length
            ? { strategy: 'all', filters: [...filters] }
            : undefined,
          sortBy: convertSortStringToObject(sort),
          pageSize: 50,
          enrichmentSpec: ['requested_by'],
        },
        { debounceTime: 500 },
      );
    }
  }, [
    filters,
    isInScope,
    isStrict,
    readSearchConversations,
    searchQuery,
    sort,
  ]);

  useEffect(() => {
    setAsyncStatus(
      getAsyncStatusFromRtkQueryStates({
        isUninitialized,
        isFetching,
        isSuccess,
        isError,
      }),
    );
  }, [isError, isFetching, isSuccess, isUninitialized, setAsyncStatus]);

  useUnmount(() => setAsyncStatus('fulfilled'));

  const openPreview = useCallback((csid: number) => {
    resultsScrollTopRef.current = resultsRef.current?.scrollTop;
    setPreviewedCsid(csid);
  }, []);

  const closePreview = useCallback(() => setPreviewedCsid(undefined), []);

  const hideCommandLauncher = useCallback(
    () => dispatch(appActions.hideModal({ type: 'app:commandLauncher' })),
    [dispatch],
  );

  // Restores the list scroll position, which is lost while the list is hidden.
  useLayoutEffect(() => {
    const scrollContainer = resultsRef.current;
    if (isPreviewing || !scrollContainer) return;

    if (resultsScrollTopRef.current !== undefined) {
      scrollContainer.scrollTop = resultsScrollTopRef.current;
      resultsScrollTopRef.current = undefined;
    }
  }, [isPreviewing]);

  // A new search replaces the results, so the preview no longer belongs to them.
  useEffect(() => {
    resultsScrollTopRef.current = undefined;
    setPreviewedCsid(undefined);
  }, [searchQuery, isStrict, filters, sort]);

  const actions = useMemo(
    () =>
      allActions.filter((action) =>
        action.scope?.includes(CommandLauncherScope.Conversation),
      ),
    [allActions],
  );

  useEffect(() => {
    const section = `Conversations (${formatResultsCount(meta?.total_results)})`;

    const conversationActions: readonly CommandLauncherAction[] =
      conversations.map((conversation) => ({
        ...createAction({
          scope: [CommandLauncherScope.Conversation],
          section,
          keepOpen: true,
          performFn: () => openPreview(conversation.id),
          label: getConversationTitle({
            channel: conversation.initial_channel,
            direction: conversation.initial_direction,
            subject: conversation.subject,
            requester: conversation.requested_by,
          }),
          prefix: (
            <ConversationChip
              csid={conversation.id}
              channel={conversation.initial_channel}
              status={conversation.status}
            />
          ),
          suffix: (
            <FlexBox alignItems="center" gap="200">
              {conversation.requested_by && (
                <Tooltip
                  content={getUserDisplayName(conversation.requested_by)}
                  side="left"
                >
                  <Avatar user={conversation.requested_by} size="24" />
                </Tooltip>
              )}

              <Tooltip content="Open conversation" side="left">
                <IconButton
                  icon="external-link"
                  variant="tertiary"
                  aria-label="Open conversation"
                  onClick={(e) => {
                    e.stopPropagation();

                    navigate({
                      path: '/conversation/:csid',
                      params: { path: { csid: `${conversation.id}` } },
                    });
                    hideCommandLauncher();
                  }}
                />
              </Tooltip>
            </FlexBox>
          ),
        }),
        // Stable id keeps list items mounted (and scroll position) when pages are appended.
        id: `conversation-${conversation.id}`,
      }));

    const unregister = registerActions(conversationActions);

    return () => unregister();
  }, [
    conversations,
    createAction,
    hideCommandLauncher,
    meta?.total_results,
    navigate,
    openPreview,
    registerActions,
  ]);

  const handleSelectConversation = useCallback(
    (event: SelectionListChangeEvent<CommandLauncherAction>) => {
      if (event.selectedItem.isNode) {
        return;
      }

      performAction(event.selectedItem.value);
    },
    [performAction],
  );

  const handleChangeFilters = useCallback(
    (
      field: SearchFilterField<'conversations'>,
      { action, operator, values }: SearchFilterChangeEvent,
    ) =>
      setFilters((current) => [
        ...current.filter((condition) => condition.field !== field),
        ...(action === 'upsert'
          ? [
              {
                field,
                operator,
                values,
              } as SearchFilterCondition<'conversations'>,
            ]
          : []),
      ]),
    [],
  );

  if (!isInScope) {
    return null;
  }

  const renderEmptyState = (message: string) => (
    <Box padding="400 0" className={styles.emptyState}>
      <span>{message}</span>
    </Box>
  );

  const renderList = () => {
    if (!searchQuery) {
      return renderEmptyState(
        'Search conversations by ID, subject, messages, internal notes, assigned agent and more...',
      );
    }

    if (isUninitialized || (isFetching && !isFetchingNextPage)) {
      return renderEmptyState('Finding matching conversations...');
    }

    if ((isSuccess || isError) && !isFetching && !actions.length) {
      return renderEmptyState(
        'No conversations found. Try a different search term or check the spelling.',
      );
    }

    return (
      <Column.Row
        ref={resultsRef}
        scrollable
        noMinHeight
        noHorizontalPadding
        noVerticalPadding
        className={styles.conversationsResults}
      >
        <SelectionList
          isActive={isActive && !isPreviewing}
          onChange={handleSelectConversation}
        >
          {getCommandLauncherActionItems(actions)}

          {canFetchNextPage && <Waypoint onEnter={fetchNextPage} />}
        </SelectionList>
      </Column.Row>
    );
  };

  // Actions are registered in an effect, so wait for them to catch up with the results.
  const canFetchNextPage =
    !isPreviewing &&
    !isFetching &&
    hasNextPage &&
    actions.length === conversations.length;

  return (
    <>
      {/* Hidden rather than unmounted so the results and their state survive previewing. */}
      <Columns style={isPreviewing ? { display: 'none' } : undefined}>
        <Column>
          {searchQuery && !isUninitialized && !isLoading && (
            <Column.Row noMinHeight noHorizontalPadding noVerticalPadding>
              <ConversationScopeListToolbar
                filters={filters}
                sort={sort}
                onFilterChange={handleChangeFilters}
                onSortChange={setSort}
              />
            </Column.Row>
          )}

          {renderList()}
        </Column>
      </Columns>

      {isPreviewing && (
        <ConversationScopeListPreview
          csid={previewedCsid}
          onBack={closePreview}
          onNavigateToConversation={hideCommandLauncher}
        />
      )}
    </>
  );
};
